import { useTranslation } from "react-i18next";
import defaultContent from "../../../../content/leadershipSeminar";
import defaultCommonContent from "../../../../content/common";
import LeafTile from "../../../leaf/LeafTile/LeafTile";
import { IconShield, BADGE_TYPE, BADGE_SIZE } from "../../../icons/IconShield/IconShield";
import { AwardProgressBadge } from "../../AwardProgressBadge/AwardProgressBadge";
import { AWARD_PROGRESS } from "../../../enums/award-progress-codes";
import { QUALIFICATION_STATUS } from "../../../enums/incentive-enums";
import "./RequalifiedBadgeTile.scss";

export interface RequalifiedBadgeRequirement {
  Name: string;
  Met: boolean;
  Status: string;
  TrackingAwardRank: number;
  FirstTime: boolean;
  FirstYearReQualified: boolean;
}

export interface RequalifiedBadgeTileProps {
  requirement: RequalifiedBadgeRequirement;
  performanceYear: string;
  rankDisplayName: string;
}

export const RequalifiedBadgeTile = ({ requirement, performanceYear, rankDisplayName }: RequalifiedBadgeTileProps) => {
  const { t } = useTranslation(["leadershipSeminar", "common"]);

  // Compute badge type from status
  const badgeType =
    requirement.Status === QUALIFICATION_STATUS.QUALIFIED
      ? BADGE_TYPE.EARNED
      : requirement.Status === QUALIFICATION_STATUS.NOT_QUALIFIED ||
          requirement.Status === QUALIFICATION_STATUS.NOT_TRACKING
        ? BADGE_TYPE.NOT_EARNED
        : BADGE_TYPE.POTENTIAL;

  // Compute progress status from status
  const progressStatus =
    requirement.Status === QUALIFICATION_STATUS.QUALIFIED
      ? AWARD_PROGRESS.ACHIEVED
      : requirement.Status === QUALIFICATION_STATUS.NOT_QUALIFIED ||
          requirement.Status === QUALIFICATION_STATUS.NOT_TRACKING
        ? AWARD_PROGRESS.NOT_TRACKING
        : AWARD_PROGRESS.TRACKING;

  // Always show the full requirement title. The raw requirement name key
  // (derivedNewOrRequalifiedAward) must never leak to the UI.
  const title = String(t("pathView.firstTimeTitle", defaultContent.pathView.firstTimeTitle));

  return (
    <div className="RequalifiedBadgeTile__container">
      <LeafTile
        title={title}
        showMetStatus={true}
        met={requirement.Met}
        hideBorder={false}
        circleCheckmarkId={`requalified-badge-${requirement.Name}`}
      >
        <div className="RequalifiedBadgeTile">
          {/* Badge icon section */}
          <div className="RequalifiedBadgeTile__icon">
            <IconShield size={BADGE_SIZE.EXTRA_LARGE} type={badgeType} />
          </div>

          {/* KPI section */}
          <dl className="RequalifiedBadgeTile__kpis">
            <div className="RequalifiedBadgeTile__kpi-row RequalifiedBadgeTile__kpi-row--highlight">
              <dt>
                {String(
                  t("pathView.trackingAwardLevel", defaultContent.pathView.trackingAwardLevel, { performanceYear }),
                )}
              </dt>
              <dd>{rankDisplayName}</dd>
            </div>
            <div className="RequalifiedBadgeTile__kpi-row">
              <dt>{String(t("pathView.newAwardLevel", defaultContent.pathView.newAwardLevel))}</dt>
              <dd>
                {requirement.FirstTime
                  ? String(t("common:yes", defaultCommonContent.yes))
                  : String(t("common:no", defaultCommonContent.no))}
              </dd>
            </div>
            <div className="RequalifiedBadgeTile__kpi-row">
              <dt>
                {String(t("pathView.firstYearRequalification", defaultContent.pathView.firstYearRequalification))}
              </dt>
              <dd>
                {requirement.FirstYearReQualified
                  ? String(t("common:yes", defaultCommonContent.yes))
                  : String(t("common:no", defaultCommonContent.no))}
              </dd>
            </div>
          </dl>

          {/* Progress badge */}
          <div className="RequalifiedBadgeTile__status">
            <AwardProgressBadge status={progressStatus} hideIcon={true} />
          </div>
        </div>
      </LeafTile>
    </div>
  );
};
